import { apiAxios } from "./axios";

export interface CV {
  id: string;
  name: string;
  content: any;
  created_at: string;
  updated_at: string;
}

export interface Rates {
  rate: number;
  project_volume: number;
  lower_band: number;
  upper_band: number;
}

export interface Candidate {
  id: string;
  consultant_id?: string;
  name: string;
  email: string;
  supervisor_email?: string;
  cv_template_full?: any; // Deprecated - for backward compatibility only
  cv_template_full_2?: any; // Deprecated - for backward compatibility only
  cvs?: CV[];
  status?: string;
  from_date?: string;
  availability?: number;
  priority?: number;
  rates?: Rates;
  archived?: boolean;
}

export interface ProjectIdentifier {
  title: string;
  time_period: string;
}

export interface Match {
  id?: string;
  consultant_id: string;
  consultant_name?: string;
  consultant_email?: string;
  score: number;
  score_cv_adapted?: number;
  score_consultant_edited?: number;
  status: string;
  consultant_active?: boolean;
  consultant_confirmed?: string;
  supervisor_confirmed?: string;
  supervisor_enabled_at_confirmation?: boolean;
  candidate?: Candidate;
  email?: string;
  cv?: any;
  selected_similar_projects?: ProjectIdentifier[];
  cv_language?: "en" | "de";
  email_language?: "en" | "de";
  updated_at?: string;
  translation_status?: "in_progress" | "completed";
  email_translation_status?: "in_progress" | "completed" | "failed";
  requirementsMatch?: {
    content: any;
    show: boolean;
  };
  keyword_analysis?: {
    keywords: {
      keyword: string;
      count: number;
      source: "required" | "nice_to_have" | "tag";
    }[];
    summary: {
      total: number;
      present: number;
      missing: number;
    };
  };
  baseline_keyword_analysis?: Match["keyword_analysis"];
  metadata?: {
    cv_generation_failed?: boolean;
    cv_type?: "base" | "tailored";
    [key: string]: unknown;
  };
  recruiter_email?: string | null;
  recruiter_email_source?:
    | 'job_description'
    | 'pipedrive'
    | 'pipedrive_direct_match'
    | 'pipedrive_indirect_match'
    | 'pattern_derived'
    | null;
  recruiter_email_verification?: 'deliverable' | 'risky' | 'undeliverable' | 'unverified' | null;
  recruiter_email_pattern_method?: 'bootstrap_statistical' | 'runtime_statistical' | 'runtime_llm' | null;
  recruiter_email_pattern_sample_count?: number | null;
  email_keyword_analysis?: {
    keywords: {
      keyword: string;
      source: "required" | "nice_to_have" | "tag";
      in_email: boolean;
      in_cv: boolean;
      email_count: number;
      cv_count: number;
    }[];
    summary: {
      total: number;
      in_email: number;
      missing_from_email: number;
    };
  };
}

export interface Offer {
  id: string;
  created_at: string;
  ansprechpartner?: string;
  url: string;
  title: string;
  auslastung?: string;
  schlagworte?: string[];
  beschreibung?: string;
  beschreibung_translated?: string;
  start?: string;
  dauer?: string;
  stundensatz?: string;
  vertragsart?: string;
  einsatzart?: string;
  einsatzort?: string;
  branche?: string;
  primary_job_language?: string;
  company?: string;
  eingestellt?: string;
  provider?: string;
  scraping_failed?: boolean;
  archived_at?: string;
  matches?: Match[];
  sortedMatches?: Match[];
}

export interface PaginatedOffersResponse {
  items: Offer[];
  nextToken: string | null;
  scanLimitReached?: boolean;
}

export interface MatchData {
  data: any;
  status: string;
}

class AlqemiDataService {
  async getCandidates(): Promise<Candidate[]> {
    const response = await apiAxios.get<Candidate[]>("/candidates");
    return response.data;
  }

  async getCandidate(id: string): Promise<Candidate> {
    const response = await apiAxios.get<Candidate>(`/candidate/${id}`);
    return response.data;
  }

  async updateCandidateStatus(
    id: string,
    status: string,
    from_date?: string
  ): Promise<void> {
    await apiAxios.post("/update-candidate-status", {
      id,
      status,
      from_date,
    });
  }

  async updateCandidate(
    id: string,
    property: string,
    value: any
  ): Promise<void> {
    await apiAxios.post("/update-candidate", {
      id,
      property,
      value,
    });
  }

  async addCV(
    candidateId: string,
    cvName: string,
    cvContent: any
  ): Promise<void> {
    const candidate = await this.getCandidate(candidateId);
    const cvs = candidate.cvs || [];

    const newCV: CV = {
      id: crypto.randomUUID(),
      name: cvName,
      content: cvContent,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    if (cvs.length >= 10) {
      throw new Error("Maximum 10 CVs allowed per consultant");
    }

    cvs.push(newCV);

    await this.updateCandidate(candidateId, "cvs", cvs);
  }

  async updateCV(
    candidateId: string,
    cvId: string,
    cvContent: any
  ): Promise<void> {
    const candidate = await this.getCandidate(candidateId);
    const cvs = candidate.cvs || [];

    const cvIndex = cvs.findIndex((cv) => cv.id === cvId);
    if (cvIndex === -1) {
      throw new Error("CV not found");
    }

    cvs[cvIndex] = {
      ...cvs[cvIndex],
      content: cvContent,
      updated_at: new Date().toISOString(),
    };

    await this.updateCandidate(candidateId, "cvs", cvs);
  }

  async updateCVName(
    candidateId: string,
    cvId: string,
    name: string
  ): Promise<void> {
    const candidate = await this.getCandidate(candidateId);
    const cvs = candidate.cvs || [];

    const cvIndex = cvs.findIndex((cv) => cv.id === cvId);
    if (cvIndex === -1) {
      throw new Error("CV not found");
    }

    cvs[cvIndex] = {
      ...cvs[cvIndex],
      name: name,
      updated_at: new Date().toISOString(),
    };

    await this.updateCandidate(candidateId, "cvs", cvs);
  }

  async deleteCV(candidateId: string, cvId: string): Promise<void> {
    const candidate = await this.getCandidate(candidateId);
    const cvs = candidate.cvs || [];

    const updatedCvs = cvs.filter((cv) => cv.id !== cvId);

    if (updatedCvs.length === cvs.length) {
      throw new Error("CV not found");
    }

    await this.updateCandidate(candidateId, "cvs", updatedCvs);
  }

  async addProjectToAllCVs(candidateId: string, project: any): Promise<void> {
    await apiAxios.post(`/consultants/${candidateId}/projects`, {
      project,
    });
  }

  async updateCandidatePriority(id: string, priority: number): Promise<void> {
    await apiAxios.patch(`/consultants/${id}/priority`, {
      priority,
    });
  }

  async getCandidateRates(id: string): Promise<Rates> {
    const response = await apiAxios.get<{ consultant_id: string; rates: Rates }>(
      `/consultants/${id}/rates`,
    );
    return response.data.rates;
  }

  async updateCandidateRates(id: string, rates: Rates): Promise<void> {
    await apiAxios.patch(`/consultants/${id}/rates`, { rates });
  }

  async getOffers(params?: {
    showOnlyTopOffers?: boolean;
    searchQuery?: string;
    currentCandidate?: string;
    includeArchived?: boolean;
  }): Promise<Offer[]> {
    const response = await apiAxios.get<Offer[]>("/offers", { params });
    return response.data;
  }

  async getArchivedOffersPaginated(params?: {
    pageSize?: number;
    nextToken?: string;
    searchQuery?: string;
    currentCandidate?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<PaginatedOffersResponse> {
    const response = await apiAxios.get<PaginatedOffersResponse>("/offers", {
      params: {
        includeArchived: true,
        ...params,
      },
    });
    return response.data;
  }

  async getMatchStats(): Promise<{
    all: { counts: Record<string, number>; total: number };
    since_feb: { counts: Record<string, number>; total: number };
  }> {
    const response = await apiAxios.get<{
      all: { counts: Record<string, number>; total: number };
      since_feb: { counts: Record<string, number>; total: number };
    }>("/match-stats");
    return response.data;
  }

  async getOffer(id: string): Promise<Offer> {
    const response = await apiAxios.get<Offer>(`/offer/${id}`);
    return response.data;
  }

  async updateMatch(id: string, property: string, value: any): Promise<void> {
    await apiAxios.post("/update-match", {
      id,
      property,
      value,
    });
  }

  async getMatch(id: string, previousUpdatedAt?: string): Promise<MatchData> {
    const params = previousUpdatedAt ? { previousUpdatedAt } : {};
    const response = await apiAxios.get<MatchData>(`/match/${id}`, { params });
    return response.data;
  }

  async confirmMatch(
    id: string,
    is_supervisor: boolean,
    confirmation: boolean = true,
    cvPdf?: string
  ): Promise<void> {
    await apiAxios.post(`/confirm-match/${id}`, {
      is_supervisor,
      confirmation,
      ...(cvPdf ? { cvPdf } : {}),
    });
  }

  async triggerJobScraping(
    search_topics: string[],
    provider: string = "freelancermap"
  ): Promise<any> {
    const response = await apiAxios.post("/trigger-job-scraping", {
      search_topics,
      provider,
    });
    return response.data;
  }

  async triggerMatching(
    use_vector_search: boolean = false,
    job_limit: number = 10
  ): Promise<any> {
    const response = await apiAxios.post("/trigger-matching", {
      use_vector_search,
      job_limit,
    });
    return response.data;
  }

  async getMatchingStatus(): Promise<any> {
    const response = await apiAxios.get("/matching-status");
    return response.data;
  }

  async translateCV(
    match_id: string,
    target_language: "en" | "de"
  ): Promise<any> {
    const response = await apiAxios.post("/translate-cv", {
      match_id,
      target_language,
    });
    return response.data;
  }

  async translateEmail(
    match_id: string,
    target_language: "en" | "de"
  ): Promise<any> {
    const response = await apiAxios.post("/translate-email", {
      match_id,
      target_language,
    });
    return response.data;
  }

  async translateDescription(offerId: string): Promise<{ translated: string }> {
    const response = await apiAxios.post<{ translated: string }>("/translate-description", {
      offer_id: offerId,
    });
    return response.data;
  }

  async markMatchAsSent(matchId: string): Promise<any> {
    try {
      const response = await apiAxios.post(
        `/alqemi/matches/${matchId}/mark-sent`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to mark match as sent:", error);
      // Fall back to just updating the status if the new endpoint doesn't exist
      await this.updateMatch(matchId, "status", "sent");
      return { success: true, message: "Match marked as sent" };
    }
  }

  async syncToPipedrive(matchId: string): Promise<any> {
    try {
      const response = await apiAxios.post("/alqemi/pipedrive/sync-deal", {
        match_id: matchId,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to sync to Pipedrive:", error);
      return null;
    }
  }

  async getPipedriveStatus(): Promise<any> {
    try {
      const response = await apiAxios.get("/alqemi/pipedrive/status");
      return response.data;
    } catch (error) {
      console.error("Failed to get Pipedrive status:", error);
      return { configured: false };
    }
  }

  async generateEmail(match_id: string): Promise<any> {
    try {
      const response = await apiAxios.post(`/generate-email/${match_id}`);
      return response.data;
    } catch (error) {
      console.error("Failed to generate email:", error);
      throw error;
    }
  }

  async refreshEmailKeywords(matchId: string): Promise<any> {
    try {
      const response = await apiAxios.post(`/refresh-email-keywords/${matchId}`);
      return response.data;
    } catch (error) {
      console.error("Failed to refresh email keywords:", error);
      throw error;
    }
  }

  async saveCv(
    matchId: string,
    cv: CV,
  ): Promise<{
    success: boolean;
    keyword_analysis: Match["keyword_analysis"] | null;
    warning?: string;
  }> {
    try {
      const response = await apiAxios.post(`/save-cv/${matchId}`, { cv });
      return response.data;
    } catch (error) {
      console.error("Failed to save CV:", error);
      throw error;
    }
  }

  async rejectMatch(matchId: string): Promise<any> {
    try {
      const response = await apiAxios.post(`/alqemi/matches/${matchId}/reject`);
      return response.data;
    } catch (error) {
      console.error("Failed to reject match:", error);
      throw error;
    }
  }

  async rescoreMatch(matchId: string): Promise<any> {
    try {
      const response = await apiAxios.post(`/rescore-match/${matchId}`);
      return response.data;
    } catch (error) {
      console.error("Failed to re-score match:", error);
      throw error;
    }
  }
}

const alqemiDataService = new AlqemiDataService();
export default alqemiDataService;
